import { LocationPositionCard } from '@/components/modules/LocationPositionCard';
import { useKakaoMap } from '@/hooks/useKakaoMap';
import type { Position, Site } from '@/types/site';

interface LocationDetailTabProps {
  site: Site;
  expandedMaps: { [key: string]: boolean };
  toggleMap: (key: string) => void;
}

export default function LocationDetailTab({
  site,
  expandedMaps,
  toggleMap,
}: Readonly<LocationDetailTabProps>) {
  // 배수지 지도들 (최대 3개까지 지원)
  const { mapRef: waterSupply0MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[0] && expandedMaps['waterSupply-0'],
    site?.waterSupplyPosition?.[0]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[0].latitude,
              longitude: site.waterSupplyPosition[0].longitude,
              name: site.waterSupplyPosition[0].name,
              address: site.waterSupplyPosition[0].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[0].latitude,
            longitude: site.waterSupplyPosition[0].longitude,
          },
        }
      : undefined
  );

  const { mapRef: waterSupply1MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[1] && expandedMaps['waterSupply-1'],
    site?.waterSupplyPosition?.[1]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[1].latitude,
              longitude: site.waterSupplyPosition[1].longitude,
              name: site.waterSupplyPosition[1].name,
              address: site.waterSupplyPosition[1].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[1].latitude,
            longitude: site.waterSupplyPosition[1].longitude,
          },
        }
      : undefined
  );

  const { mapRef: waterSupply2MapRef } = useKakaoMap(
    !!site && !!site?.waterSupplyPosition?.[2] && expandedMaps['waterSupply-2'],
    site?.waterSupplyPosition?.[2]
      ? {
          positions: [
            {
              latitude: site.waterSupplyPosition[2].latitude,
              longitude: site.waterSupplyPosition[2].longitude,
              name: site.waterSupplyPosition[2].name,
              address: site.waterSupplyPosition[2].address,
              type: 'waterSupply' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.waterSupplyPosition[2].latitude,
            longitude: site.waterSupplyPosition[2].longitude,
          },
        }
      : undefined
  );

  const getWaterSupplyMapRef = (index: number) => {
    switch (index) {
      case 0:
        return waterSupply0MapRef;
      case 1:
        return waterSupply1MapRef;
      case 2:
        return waterSupply2MapRef;
      default:
        return waterSupply0MapRef;
    }
  };

  const { mapRef: wellMapRef } = useKakaoMap(
    true,
    site
      ? {
          positions: [
            {
              latitude: site.wellPosition.latitude,
              longitude: site.wellPosition.longitude,
              name: site.wellPosition.name,
              address: site.wellPosition.address,
              type: 'well' as const,
            },
          ],
          level: 3,
          center: {
            latitude: site.wellPosition.latitude,
            longitude: site.wellPosition.longitude,
          },
        }
      : undefined
  );
  return (
    <div className="space-y-6">
      {/* 주소 정보 카드 */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">주소 정보</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h5 className="text-sm font-semibold text-gray-900 mb-2">
              관정 주소
            </h5>
            <p className="text-sm text-gray-600">{site.wellPosition.address}</p>
          </div>
          {/* {site.managementPosition && (
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">
                관리사 주소
              </h5>
              <p className="text-sm text-gray-600">
                {site.managementPosition.address}
              </p>
            </div>
          )} */}
          {site.waterSupplyPosition &&
            site.waterSupplyPosition.length > 0 &&
            site.waterSupplyPosition.map((position, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 border border-green-200"
              >
                <h5 className="text-sm font-semibold text-gray-900 mb-2">
                  배수지 {index + 1} 주소
                </h5>
                <p className="text-sm text-gray-600">{position.address}</p>
              </div>
            ))}
        </div>
      </div>

      {/* 위치 정보 상세 */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-gray-900">
            위치 정보 및 지도
          </h4>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-gray-600">취수원</span>
            </div>
            {site.managementPosition && (
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                <span className="text-gray-600">관리사</span>
              </div>
            )}
            {site.waterSupplyPosition &&
              site.waterSupplyPosition.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">배수지</span>
                </div>
              )}
          </div>
        </div>

        <div className="space-y-4">
          {/* 관정 위치 */}
          <LocationPositionCard
            color="blue"
            label="관정"
            position={site.wellPosition}
            mapKey="well"
            isMapExpanded={expandedMaps['well']}
            mapRef={wellMapRef}
            onToggleMap={toggleMap}
          />

          {/* 급수지 위치들 */}
          {site.waterSupplyPosition && site.waterSupplyPosition.length > 0 && (
            <div className="space-y-3">
              {site.waterSupplyPosition.map(
                (position: Position, index: number) => (
                  <LocationPositionCard
                    key={index}
                    color="green"
                    label={`급수지 ${index + 1}`}
                    position={position}
                    mapKey={`waterSupply-${index}`}
                    isMapExpanded={expandedMaps[`waterSupply-${index}`]}
                    mapRef={getWaterSupplyMapRef(index)}
                    onToggleMap={toggleMap}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
